import { useState } from "react";
import { Button } from "../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { useForm } from "react-hook-form";
import { passwordSchema, UserPasswordFormValues } from "./user-zod-schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../ui/use-toast";
import { PasswordToggle } from "../../../lib/showPassword";
import { updatePassword } from "../../../API/Users/userService";

export function PasswordChange() {
  const form = useForm<UserPasswordFormValues>({
    resolver: zodResolver(passwordSchema),
  });
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const router = useNavigate();
  const { toast } = useToast();
  const onSubmit = async (data: UserPasswordFormValues) => {
    try {
      setLoading(true);
      await updatePassword(
        {
          oldPassword: data.oldPassword,
          newPassword: data.newPassword,
          confirmPassword: data.confirmPassword,
        },
        router
      );
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Une erreur s'est produite.",
        description:
          error.message || "Il y a eu un problème avec votre requête.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog defaultOpen={true}>
      <DialogContent className="">
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Modifier votre mot de passe</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid items-center grid-cols-2">
              <Label htmlFor="oldPassword">Mot de passe actuel</Label>
              <div className="relative">
                <Input
                  id="oldPassword"
                  type={showOldPassword ? "text" : "password"}
                  defaultValue="@password"
                  {...form.register("oldPassword")}
                />
                <PasswordToggle
                  showPassword={showOldPassword}
                  setShowPassword={setShowOldPassword}
                />
              </div>
            </div>
            {form.formState.errors.oldPassword && (
              <span className="text-sm text-red-500">
                {form.formState.errors.oldPassword.message}
              </span>
            )}
            <div className="grid items-center grid-cols-2">
              <Label htmlFor="currentPassword">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showNewPassword ? "text" : "password"}
                  defaultValue="@password"
                  {...form.register("newPassword")}
                />

                <PasswordToggle
                  showPassword={showNewPassword}
                  setShowPassword={setShowNewPassword}
                />
              </div>
            </div>
            {form.formState.errors.newPassword && (
              <span className="text-sm text-red-500">
                {form.formState.errors.newPassword.message}
              </span>
            )}
            <div className="grid items-center grid-cols-2">
              <Label htmlFor="newPassword">Confirmer le nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  defaultValue="@password"
                  {...form.register("confirmPassword")}
                />
                <PasswordToggle
                  showPassword={showConfirmPassword}
                  setShowPassword={setShowConfirmPassword}
                />
              </div>
            </div>
            {form.formState.errors.confirmPassword && (
              <span className="text-sm text-red-500">
                {form.formState.errors.confirmPassword.message}
              </span>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" isLoading={loading}>
              Modifier
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
